import PropTypes from 'prop-types';
import styled from 'styled-components';
import { GridMixin } from '../../mixins/components/GridMixin';
import CustomPropTypes from '../../util/custom-prop-types';
import { doNotForwardTransientProp } from '../../util/doNotForwardTransientProp';

/**
 * A component implementation of standard CSS Grid.
 * No CSS will be added and browser defaults will be used unless you set a prop.
 * All props are camel-cased CSS attributes. By default,
 * each `Grid` component uses a `div` -- if needed, you can use the styled-components
 * [`as`](https://www.styled-components.com/docs/api#as-polymorphic-prop) prop to change
 * the element type.
 *
 * IE11 follows an older version of the grid specification, and as such, not all functionality is
 * supported. See [IE11-compatible examples](#ie11-compatible-examples) below for additional
 * information and examples.
 *
 * **All `Grid` props support inline media query objects.**
 *
 * @related ['Flex', 'GridContent', 'GridLayout']
 * @since 8.22.0
 */
var Grid = /*#__PURE__*/styled('div').withConfig({
  shouldForwardProp: doNotForwardTransientProp(['display', 'order'])
}).withConfig({
  displayName: "Grid",
  componentId: "c11n-8-111-1__sc-18zzowe-0"
})(["", ""], GridMixin);
Grid.propTypes = {
  /** *Property for the Parent (Grid Container)* */
  display: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['grid', 'inline-grid', 'none'])),
  /** *Property for the Parent (Grid Container)* */
  gridTemplateColumns: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  gridTemplateRows: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  gridTemplateAreas: CustomPropTypes.inlineMediaQuery(PropTypes.arrayOf(PropTypes.string)),
  /** *Property for the Parent (Grid Container)* */
  gridTemplate: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * *Property for the Parent (Grid Container)*
   *
   * A spacing value
   */
  columnGap: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * *Property for the Parent (Grid Container)*
   *
   * A spacing value
   */
  rowGap: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * *Property for the Parent (Grid Container)*
   *
   * @deprecated Use `columnGap`
   */
  gridColumnGap: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * (grid containers)
   *
   * @deprecated Use `rowGap`
   */
  gridRowGap: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * *Property for the Parent (Grid Container)*
   *
   * A spacing value
   */
  gap: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * *Property for the Parent (Grid Container)*
   *
   * @deprecated Use `gap`
   */
  gridGap: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  justifyItems: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center', 'stretch'])),
  /** *Property for the Parent (Grid Container)* */
  alignItems: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center', 'stretch'])),
  /** *Property for the Parent (Grid Container)* */
  placeItems: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  justifyContent: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center', 'stretch', 'space-around', 'space-between', 'space-evenly'])),
  /** *Property for the Parent (Grid Container)* */
  alignContent: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center', 'stretch', 'space-around', 'space-between', 'space-evenly'])),
  /** *Property for the Parent (Grid Container)* */
  placeContent: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  gridAutoColumns: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  gridAutoRows: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  gridAutoFlow: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for the Parent (Grid Container)* */
  grid: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridColumnStart: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridColumnEnd: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridRowStart: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridRowEnd: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridColumn: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridRow: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  gridArea: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  justifySelf: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center', 'stretch'])),
  /** *Property for Children (Grid Items)* */
  alignSelf: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center', 'stretch'])),
  /** *Property for Children (Grid Items)* */
  placeSelf: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** *Property for Children (Grid Items)* */
  order: CustomPropTypes.inlineMediaQuery(PropTypes.number)
};

/** @component */
export { Grid };